import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ResponseErrorPanel } from '@backstage/core-components';
import { FullPage } from '@backstage/ui';
import Box from '@mui/material/Box';
import { LeftPane } from '../LeftPane/LeftPane';
import { ChatContainer, type ChatContainerRef } from '../ChatContainer';
import {
  // useProviderStatus,
  useMcpServers,
  useConversations,
  useAvailableTools,
} from '../../hooks';
import type { ConversationRecord } from '../../types';
import { useChatPageStyles } from './new-chat-page.styles';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { mcpChatTranslationRef } from '../../translation';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  tools?: string[];
  toolsUsed?: string[];
  toolResponses?: any[];
}

export const NewChatPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useChatPageStyles();
  const { t } = useTranslationRef(mcpChatTranslationRef);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const {
    mcpServers,
    error: mcpServersError,
    isLoading: mcpServersLoading,
    // handleServerToggle,
  } = useMcpServers();

  const { availableTools, isLoading: toolsLoading } =
    useAvailableTools(mcpServers);

  const {
    starredConversations,
    recentConversations,
    loading: conversationsLoading,
    error: conversationsError,
    searchQuery,
    setSearchQuery,
    clearSearch,
    loadConversation,
    refreshConversations,
    deleteConversation,
    toggleStar,
    renameConversation,
  } = useConversations();

  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<
    string | undefined
  >();
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | undefined
  >();
  const chatContainerRef = useRef<ChatContainerRef>(null);
  const conversationLoadRequestRef = useRef(0);
  const locationState = location.state as { pendingPrompt?: unknown } | null;
  const pendingPromptRef = useRef(
    typeof locationState?.pendingPrompt === 'string'
      ? locationState.pendingPrompt
      : undefined,
  );
  const initialConversationIdRef = useRef(
    new URLSearchParams(window.location.search).get('conversationId'),
  );
  const [initialConversationLoading, setInitialConversationLoading] = useState(
    Boolean(initialConversationIdRef.current),
  );

  const combinedError = error || mcpServersError;

  const handleNewChat = () => {
    conversationLoadRequestRef.current += 1;
    if (chatContainerRef.current) {
      chatContainerRef.current.cancelOngoingRequest();
    }
    setError(null);
    setMessages([]);
    setCurrentConversationId(undefined);
    setSelectedConversationId(undefined);
  };

  const handleMessagesChange = (newMessages: Message[]) => {
    conversationLoadRequestRef.current += 1;
    setMessages(newMessages);
  };

  const handleConversationUpdated = (conversationId: string) => {
    setCurrentConversationId(conversationId);
    if (!selectedConversationId) {
      setSelectedConversationId(conversationId);
    }
    refreshConversations();
  };

  useEffect(() => {
    const conversationId = initialConversationIdRef.current;
    if (!conversationId) return;
    initialConversationIdRef.current = null;
    const requestId = ++conversationLoadRequestRef.current;

    loadConversation(conversationId)
      .then(fullConversation => {
        if (requestId !== conversationLoadRequestRef.current) return;
        setMessages(
          fullConversation.messages.map((msg, index) => ({
            id: `${conversationId}-${index}`,
            text: msg.content,
            isUser: msg.role === 'user',
            timestamp: new Date(fullConversation.updatedAt),
            toolsUsed:
              msg.role === 'assistant' ? fullConversation.toolsUsed : undefined,
          })),
        );
        setSelectedConversationId(conversationId);
        setCurrentConversationId(conversationId);
        setInitialConversationLoading(false);
      })
      .catch(err => {
        if (requestId !== conversationLoadRequestRef.current) return;
        setError(`${t('chatContainer.errors.loadConversation')}: ${err}`);
        setInitialConversationLoading(false);
      });
  }, [loadConversation, t]);

  useEffect(() => {
    const pendingPrompt = pendingPromptRef.current;
    if (
      !pendingPrompt ||
      initialConversationLoading ||
      mcpServersLoading ||
      combinedError
    ) {
      return;
    }

    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    pendingPromptRef.current = undefined;
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: null,
    });
    chatContainer.sendMessage(pendingPrompt);
  }, [
    combinedError,
    initialConversationLoading,
    location,
    mcpServersLoading,
    navigate,
  ]);

  const handleDeleteConversation = async (id: string) => {
    try {
      await deleteConversation(id);

      if (currentConversationId === id || selectedConversationId === id) {
        if (chatContainerRef.current) {
          chatContainerRef.current.cancelOngoingRequest();
        }

        setMessages([]);
        setCurrentConversationId(undefined);
        setSelectedConversationId(undefined);
        setError(null);
      }
    } catch (err) {
      setError(`${t('chatContainer.errors.loadConversation')}: ${err}`);
    }
  };

  const handleSelectConversation = async (conversation: ConversationRecord) => {
    const requestId = ++conversationLoadRequestRef.current;
    try {
      if (chatContainerRef.current) {
        chatContainerRef.current.cancelOngoingRequest();
      }
      const fullConversation = await loadConversation(conversation.id);
      if (requestId !== conversationLoadRequestRef.current) return;
      const uiMessages: Message[] = fullConversation.messages.map(
        (msg, index) => ({
          id: `${conversation.id}-${index}`,
          text: msg.content,
          isUser: msg.role === 'user',
          timestamp: new Date(fullConversation.updatedAt),
          toolsUsed:
            msg.role === 'assistant' ? fullConversation.toolsUsed : undefined,
        }),
      );
      setMessages(uiMessages);
      setSelectedConversationId(conversation.id);
      setCurrentConversationId(conversation.id);
      setError(null);
    } catch (err) {
      if (requestId !== conversationLoadRequestRef.current) return;
      setError(`${t('chatContainer.errors.loadConversation')}: ${err}`);
    }
  };

  return (
    <FullPage className={classes.contentWrapper}>
      <Box className={classes.bodyContainer}>
        <LeftPane
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(prev => !prev)}
          onNewChat={handleNewChat}
          starredConversations={starredConversations}
          recentConversations={recentConversations}
          conversationsLoading={conversationsLoading}
          conversationsError={conversationsError}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearchClear={clearSearch}
          onSelectConversation={handleSelectConversation}
          onToggleStar={toggleStar}
          onDeleteConversation={handleDeleteConversation}
          onRenameConversation={renameConversation}
          selectedConversationId={selectedConversationId}
          availableTools={availableTools}
          toolsLoading={toolsLoading}
        />
        <Box className={classes.chatContainer}>
          {combinedError ? (
            <Box className={classes.errorContainer}>
              <ResponseErrorPanel
                error={new Error(combinedError)}
                defaultExpanded
              />
            </Box>
          ) : (
            <ChatContainer
              ref={chatContainerRef}
              sidebarCollapsed={sidebarCollapsed}
              mcpServers={mcpServers}
              messages={messages}
              onMessagesChange={handleMessagesChange}
              disabled={initialConversationLoading}
              conversationId={currentConversationId}
              onConversationUpdated={handleConversationUpdated}
              hideRightPaneMargin
            />
          )}
        </Box>
      </Box>
    </FullPage>
  );
};
